package com.exura.features.plandefinition.domain.model.series

import com.exura.features.plandefinition.domain.model.element.InstructionElement
import dev.gitlive.firebase.firestore.CollectionReference

/**
 * A series that generates instruction elements.
 * Implements SerializableDomainModel for Firebase storage through BaseAnchoredSeries.
 */
data class SimpleInstructionSeries(
    override val firebaseDocId: String? = null,
    override val firebaseDocPrefix: CollectionReference? = null,
    override val title: String,
    override val description: String,
    override val relativeDay: Int,
    override val elements: List<InstructionElement> = emptyList(),
    val spawnForDays: Int? = null,
    val spawnCount: Int? = null,
    val instruction: String,
    val estimatedMinutes: Int,
    val partsOfDay: List<PartOfDay>? = null,
    val exactTimes: List<HourOfDay>? = null,
    override val imageUrl: String? = null,
    val spawnEveryDays: Int = 1
) : BaseAnchoredSeries<InstructionElement>(
    firebaseDocId, firebaseDocPrefix, SeriesType.SIMPLE_INSTRUCTION.spawnType,
) {

    init {
        require(spawnEveryDays > 0) { "spawnEveryDays must be greater than 0" }

        require((partsOfDay == null) != (exactTimes == null)) {
            "Either partsOfDay or exactTimes must be provided, but not both"
        }

        require((spawnForDays == null) != (spawnCount == null)) {
            "Exactly one of spawnForDays or spawnCount must be specified"
        }
    }

    override fun spawnElements(): List<InstructionElement> {
        val elementCount = if (spawnForDays != null) {
            // Determine how many elements fit within the specified days
            spawnForDays / spawnEveryDays
        } else {
            // Use spawnCount directly
            spawnCount ?: 0
        }

        return (0 until elementCount).flatMap { index ->
            val dayOffset = index * spawnEveryDays
            val elementDay = relativeDay + dayOffset

            createElementsForDay(elementDay)
        }
    }

    private fun createElementsForDay(day: Int): List<InstructionElement> {
        return when {
            !partsOfDay.isNullOrEmpty() -> {
                partsOfDay.map { createInstructionElement(day, partOfDay = it) }
            }
            !exactTimes.isNullOrEmpty() -> {
                exactTimes.map { createInstructionElement(day, exactTime = it) }
            }
            else -> {
                listOf(createInstructionElement(day))
            }
        }
    }

    private fun createInstructionElement(
        day: Int,
        partOfDay: PartOfDay? = null,
        exactTime: HourOfDay? = null
    ): InstructionElement {
        return InstructionElement(
            relativeDay = day,
            title = instruction,
            instruction = instruction,
            durationInMinutes = estimatedMinutes,
            partOfDay = partOfDay,
            exactTime = exactTime,
            imageUrl = imageUrl
        )
    }

    override fun durationInDays(): Int {
        val elementCount = if (spawnForDays != null) {
            spawnForDays / spawnEveryDays
        } else {
            spawnCount ?: 0
        }

        return if (elementCount > 0) {
            (elementCount - 1) * spawnEveryDays + 1
        } else {
            0
        }
    }
}
